import React, { useState, useEffect, useRef } from 'react';
import { useBuoyLightSettings } from '../../hooks/useBuoyLightSettings';
import BuoyLightCard from './BuoyLightCard';
import { T } from './accountStyles';

function AccessPill({ isOwner }) {
  const style = isOwner
    ? { bg: T.greenPillBg, text: T.green, dot: T.greenDot, label: 'Administrator · you can edit' }
    : { bg: T.greyPillBg, text: T.greyPillText, dot: T.greyDot, label: 'View only' };
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, background: style.bg, color: style.text, padding: '5px 12px', borderRadius: 999, fontSize: 13, fontWeight: 600 }}>
      <span style={{ width: 8, height: 8, borderRadius: '50%', background: style.dot }} />
      {style.label}
    </span>
  );
}

function Toast({ message }) {
  if (!message) return null;
  return (
    <div style={{
      position: 'fixed', left: '50%', bottom: 28, transform: 'translateX(-50%)',
      background: '#22394b', color: '#fff', padding: '12px 20px', borderRadius: 999,
      fontSize: 14.5, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 10,
      boxShadow: '0 8px 28px rgba(0,0,0,0.28)', zIndex: 2000, fontFamily: T.body,
    }}>
      <span style={{ width: 9, height: 9, borderRadius: '50%', background: '#4fbf6c' }} />
      {message}
    </div>
  );
}

export default function BuoyLightSection({ grids, token, user }) {
  const { groups, loading, error, patchBuoy } = useBuoyLightSettings({ grids, token });
  const [toast, setToast] = useState(null);
  const toastTimer = useRef(null);

  const showToast = (msg) => {
    setToast(msg);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 2800);
  };
  useEffect(() => () => { if (toastTimer.current) clearTimeout(toastTimer.current); }, []);

  return (
    <section style={{ marginTop: 40 }}>
      <h2 style={{ fontFamily: T.serif, fontSize: 28, fontWeight: 700, color: T.textHeading, margin: '0 0 6px' }}>
        Buoy light management
      </h2>
      <p style={{ fontSize: 15, color: T.textMuted, margin: '0 0 24px', maxWidth: 640 }}>
        Brightness, light mode, and schedule for each active buoy you can access. You can edit buoys in grids where you're an administrator; others are view-only.
      </p>

      {loading && (
        <div style={{ color: T.textMuted, fontSize: 15 }}>Loading buoy light settings…</div>
      )}

      {!loading && error && (
        <div style={{ color: T.error, fontSize: 15 }}>Couldn't load buoy light settings: {error}</div>
      )}

      {!loading && !error && groups.length === 0 && (
        <div style={{ color: T.textMuted, fontSize: 15 }}>No active buoys are available on your grids.</div>
      )}

      <div style={{ maxWidth: 640 }}>
        {groups.map(({ grid, buoys }) => (
          <div key={grid.id} style={{ marginBottom: 34 }}>
            {/* Grid context row */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
              <h3 style={{ fontFamily: T.serif, fontSize: 19, fontWeight: 600, color: T.textHeading, margin: 0 }}>{grid.name}</h3>
              <AccessPill isOwner={grid.is_owner} />
              {(grid.city || grid.region) && (
                <span style={{ fontSize: 13.5, color: T.textMuted }}>
                  📍 {[grid.city, grid.region].filter(Boolean).join(', ')}
                </span>
              )}
            </div>

            {buoys.map((buoy) => (
              <BuoyLightCard
                key={buoy.mooringId}
                buoy={buoy}
                timezone={grid.timezone}
                isOwner={grid.is_owner}
                token={token}
                onSaved={patchBuoy}
                onToast={showToast}
              />
            ))}
          </div>
        ))}
      </div>

      <Toast message={toast} />
    </section>
  );
}
